import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { BadgeCheck } from 'lucide-react'
import { Switch } from '@/src/renderer/components/ui/switch'
import { Button } from '@/src/renderer/components/ui/Button'
import { useAccountSource } from '@/src/renderer/lib/accountSource'
import { relativeSync, type RelativeSync } from '@/src/renderer/lib/sync/lastSynced'
import { SettingsSection, SettingsRow } from '../../primitives'
import happyCloudSyncImg from '@/src/renderer/components/common/HappyCloudEntryCard/assets/happycloud-sync.png'
import './SyncPage.scss'

interface SyncPageProps {
  /** Host platform — Web is cloud-only (sync always on); Electron offers the
   *  offline-first toggle. */
  platform: 'electron' | 'web'
}

/**
 * "Synchronisation" page under "Konto & Daten". HappyCloud-Sync is a paid
 * add-on whose on/off truth lives in the cloud (`profiles.sync_entitlement`).
 *
 * Electron states:
 *  1. No account → hint to connect a HappyCloud account first.
 *  2. Authenticated → a toggle that reflects AND writes `sync_entitlement`
 *     (beta: self-activation is free). Reading it from the cloud means the
 *     switch survives logout/login and is consistent across devices.
 *
 * On Web there is no local-only mode (handled by the /account flow elsewhere),
 * so this page shows the read-only "always on" note.
 *
 * NOTE: The standalone Electron preferences window has its own React root
 * without SyncProvider/AuthProvider. So this page reads/writes via the cached
 * Supabase session (auth) and the `profiles` queries (entitlement). Flipping
 * the entitlement also nudges `storageMode`, whose `storage-mode-changed`
 * broadcast makes the main window's SyncProvider re-check the entitlement and
 * start/stop syncing live.
 */
export function SyncPage({ platform }: SyncPageProps) {
  const { t, i18n } = useTranslation()
  const account = useAccountSource()
  const [userId, setUserId] = useState<string | null>(null)
  const [syncEntitlement, setSyncEntitlementState] = useState(false)
  // Gate the control on the first resolve so it mounts already in its resolved
  // state (no off→on slide, no late pop-in). A reserved-size placeholder holds
  // the slot meanwhile.
  const [isLoaded, setIsLoaded] = useState(false)
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null)
  const [now, setNow] = useState(() => new Date())

  const isAuthenticated = userId !== null
  // Sync is live only on Electron with an account + a booked entitlement.
  const syncActive = isLoaded && isAuthenticated && syncEntitlement && platform === 'electron'

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const uid = await account.getUserId()
        if (cancelled) return
        setUserId(uid)
        if (uid) {
          const entitled = await account.getSyncEntitlement(uid)
          if (!cancelled) setSyncEntitlementState(entitled)
        }
      } catch (err) {
        console.error('[Sync] Failed to load sync state:', err)
      } finally {
        if (!cancelled) setIsLoaded(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [account])

  // "Last synced" — read the timestamp the sync engine persists to Dexie (shared
  // across windows, so this prefs window sees what the main window wrote). Refresh
  // on a timer (also advances `now`, so "just now" rolls over to "1 minute ago")
  // and on the `storage-mode-changed` broadcast (a toggle flip / a fresh sync).
  // Only while sync is actually live.
  useEffect(() => {
    if (!syncActive) {
      setLastSyncedAt(null)
      return
    }
    let active = true
    const refresh = async () => {
      const at = await account.getLastSyncedAt()
      if (active) {
        setLastSyncedAt(at)
        setNow(new Date())
      }
    }
    refresh()
    const interval = setInterval(refresh, 30_000)
    const stopListening = account.onSyncSettingsChanged(() => {
      refresh()
    })
    return () => {
      active = false
      clearInterval(interval)
      stopListening()
    }
  }, [syncActive, account])

  const formatRelativeSync = (rel: RelativeSync): string => {
    switch (rel.kind) {
      case 'justNow':
        return t('settings.sync.lastSynced.justNow')
      case 'minutes':
        return t(
          rel.count === 1 ? 'settings.sync.lastSynced.minutesAgoOne' : 'settings.sync.lastSynced.minutesAgoOther',
          { count: rel.count },
        )
      case 'hours':
        return t(
          rel.count === 1 ? 'settings.sync.lastSynced.hoursAgoOne' : 'settings.sync.lastSynced.hoursAgoOther',
          { count: rel.count },
        )
      case 'date':
        return new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' }).format(
          rel.date,
        )
    }
  }

  // The switch reflects the cloud entitlement (single source of truth). Toggling
  // writes it to the account AND nudges the local storageMode — the latter's
  // `storage-mode-changed` broadcast makes the main window re-check the
  // entitlement and start/stop syncing (and run the first upload/merge).
  const handleToggleSync = async (checked: boolean) => {
    if (!userId) return
    setSyncEntitlementState(checked) // optimistic
    try {
      const ok = await account.setSyncEntitlement(userId, checked)
      if (!ok) throw new Error('entitlement write failed')
      await account.setStorageMode(checked ? 'cloud-sync' : 'offline')
    } catch (err) {
      console.error('[Sync] Failed to update sync entitlement:', err)
      setSyncEntitlementState(!checked) // revert
    }
  }

  if (platform === 'web') {
    return (
      <div className="sync-page">
        <SettingsSection title={t('settings.sync.title')} variant="card">
          <SettingsRow
            leading={<img src={happyCloudSyncImg} alt="" className="sync-page__icon" />}
            label={t('settings.sync.webTitle')}
            description={t('settings.sync.webAlwaysOn')}
            control={null}
          />
        </SettingsSection>
      </div>
    )
  }

  // Resolve the label + description + control for the current state.
  let label: string = t('settings.sync.toggleLabel')
  let description: string = t('settings.sync.toggleDescription')
  let control: ReactNode = (
    // Reserves the control footprint while loading so the row doesn't jump.
    <span className="sync-page__toggle-placeholder" aria-hidden="true" />
  )
  let htmlFor: string | undefined

  if (isLoaded) {
    if (!isAuthenticated) {
      label = t('settings.sync.connectLabel')
      description = t('settings.sync.connectRequired')
      // No account yet → offer to connect one. The connect/login flow lives in the
      // MAIN window; this standalone preferences window asks the main process to
      // focus it and run the flow there (see the `connect-happycloud` IPC handler
      // and useConnectCloud).
      control = (
        <Button variant="outline" onClick={() => account.connectCloud()}>
          {t('settings.sync.connectCta')}
        </Button>
      )
    } else {
      description = syncEntitlement
        ? t('settings.sync.toggleDescription')
        : t('settings.sync.notBookedDescription')
      htmlFor = 'happycloud-sync-toggle'
      control = (
        <Switch
          id="happycloud-sync-toggle"
          checked={syncEntitlement}
          onCheckedChange={handleToggleSync}
        />
      )
    }
  }

  return (
    <div className="sync-page">
      <SettingsSection title={t('settings.sync.title')} variant="card">
        <SettingsRow
          htmlFor={htmlFor}
          leading={<img src={happyCloudSyncImg} alt="" className="sync-page__icon" />}
          label={label}
          description={description}
          control={control}
        />
        {/* Zweite Fläche derselben Karte, wie die übrigen Seiten unter „Profil & Daten“. */}
        {syncActive && lastSyncedAt && (
          <div className="sync-page__last-synced">
            <BadgeCheck size={20} className="sync-page__last-synced-icon" aria-hidden="true" />
            <span className="sync-page__last-synced-text">
              {t('settings.sync.lastSynced.prefix')}:{' '}
              <strong className="sync-page__last-synced-time">
                {formatRelativeSync(relativeSync(lastSyncedAt, now))}
              </strong>
            </span>
          </div>
        )}
      </SettingsSection>
    </div>
  )
}
